<template>
  <a-modal
    :open="open"
    wrap-class-name="contract-view-video-modal"
    :centered="true"
    :footer="null"
    :mask-closable="true"
    destroy-on-close
    width="1180px"
    :title="null"
    @cancel="$emit('update:open', false)"
  >
    <div v-if="guide" class="video-guide-modal-body">
      <div class="vg-header">
        <h3 class="vg-title">{{ guide.title }}</h3>
        <a-tag color="blue">{{ guide.badge }}</a-tag>
        <span class="vg-duration">{{ formatGuideDuration(guide.durationSec) }}</span>
      </div>

      <div class="vg-grid">
        <div class="vg-main">
          <video
            ref="video"
            class="vg-video"
            controls
            preload="metadata"
            playsinline
            :poster="guide.poster"
            :src="guide.video"
            @loadedmetadata="applyRate"
            @timeupdate="onTimeUpdate"
          ></video>

          <div class="vg-rate-row">
            <span class="vg-rate-label">배속</span>
            <a-radio-group v-model:value="rate" button-style="solid" size="small" @change="onRateChange">
              <a-radio-button :value="1">1.0</a-radio-button>
              <a-radio-button :value="1.25">1.25</a-radio-button>
              <a-radio-button :value="1.5">1.5</a-radio-button>
            </a-radio-group>
          </div>

          <div class="vg-steps">
            <div
              v-for="(step, i) in guide.steps"
              :key="step.n"
              class="vg-step-row"
              :class="{ active: i === activeStepIndex }"
              @click="seekTo(step.t)"
            >
              <span class="vg-step-n">{{ step.n }}</span>
              <span class="vg-step-title">{{ step.title }}</span>
              <span class="vg-step-t">{{ formatGuideDuration(step.t) }}</span>
            </div>
          </div>
        </div>

        <div class="vg-side">
          <a-card size="small">
            <div v-if="guide.when" class="vg-block">
              <div class="vg-block-label">이럴 때 봅니다</div>
              <div class="vg-block-value">{{ guide.when }}</div>
            </div>
            <div v-if="guide.before" class="vg-block">
              <div class="vg-block-label">먼저 필요한 것</div>
              <div class="vg-block-value">{{ guide.before }}</div>
            </div>
            <div v-if="guide.result" class="vg-block">
              <div class="vg-block-label">끝나면</div>
              <div class="vg-block-value">{{ guide.result }}</div>
            </div>
            <div v-if="guide.skip" class="vg-block">
              <div class="vg-block-label">건너뛰기</div>
              <div class="vg-block-value">{{ guide.skip }}</div>
            </div>

            <a-alert
              v-for="(w, idx) in guide.warnings"
              :key="idx"
              type="warning"
              show-icon
              :message="w"
              class="vg-warning"
            />
          </a-card>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { formatGuideDuration } from './videoGuide';

const RATES = [1, 1.25, 1.5];
const RATE_STORAGE_KEY = 'guide:playbackRate';

export default {
  name: 'VideoGuideModal',
  props: {
    open: { type: Boolean, default: false },
    guide: { type: Object, default: null },
  },
  emits: ['update:open'],
  data() {
    return {
      rate: 1,
      currentTime: 0,
    };
  },
  computed: {
    // 마지막 재생 위치 기준으로 활성 스텝을 찾는다 (t <= currentTime 인 마지막 스텝)
    activeStepIndex() {
      const steps = this.guide && this.guide.steps;
      if (!steps) return -1;
      let idx = -1;
      for (let i = 0; i < steps.length; i += 1) {
        if (steps[i].t <= this.currentTime) idx = i;
      }
      return idx;
    },
  },
  watch: {
    open(next) {
      if (next) {
        this.currentTime = 0;
      } else {
        const video = this.$refs.video;
        if (video) video.pause();
      }
    },
  },
  mounted() {
    // 이전 방문에서 고른 배속을 이어서 쓴다 — 세션마다 배속을 다시 누르게 하지 않는다
    try {
      const stored = Number(localStorage.getItem(RATE_STORAGE_KEY));
      if (RATES.includes(stored)) this.rate = stored;
    } catch (e) {
      // localStorage 접근 불가(프라이빗 모드 등) — 기본 배속(1x) 유지
    }
  },
  methods: {
    formatGuideDuration,
    applyRate() {
      const video = this.$refs.video;
      if (video) video.playbackRate = this.rate;
    },
    onRateChange() {
      this.applyRate();
      try {
        localStorage.setItem(RATE_STORAGE_KEY, String(this.rate));
      } catch (e) {
        // localStorage 접근 불가 — 이번 세션에서만 배속 적용
      }
    },
    onTimeUpdate() {
      const video = this.$refs.video;
      if (video) this.currentTime = video.currentTime;
    },
    seekTo(t) {
      const video = this.$refs.video;
      if (!video) return;
      video.currentTime = t;
      video.play();
    },
  },
};
</script>

<style scoped>
.video-guide-modal-body {
  padding: 20px 24px;
  max-height: 88vh;
  overflow-y: auto;
}

.vg-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.vg-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.vg-duration {
  color: #8d939a;
  font-size: 13px;
}

.vg-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 20px;
}

.vg-video {
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #0e1116;
  border-radius: 8px;
  display: block;
}

.vg-rate-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}

.vg-rate-label {
  color: #4b5158;
  font-size: 13px;
}

.vg-steps {
  margin-top: 16px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
}

.vg-step-row {
  display: grid;
  grid-template-columns: 32px 1fr 48px;
  gap: 8px;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}

.vg-step-row:last-child {
  border-bottom: none;
}

.vg-step-row:hover {
  background: #fafafa;
}

.vg-step-row.active {
  background: #e6f4ff;
}

.vg-step-n {
  color: #8d939a;
  font-weight: 600;
}

.vg-step-t {
  color: #1677ff;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.vg-block {
  margin-bottom: 14px;
}

.vg-block-label {
  font-size: 12px;
  color: #8d939a;
  margin-bottom: 4px;
}

.vg-block-value {
  font-size: 14px;
  color: #1f2329;
  white-space: pre-line;
}

.vg-warning {
  margin-top: 10px;
}

@media screen and (max-width: 1100px) {
  .vg-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<style>
.contract-view-video-modal .ant-modal-content {
  height: auto !important;
  min-height: 0 !important;
}
</style>
